import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { Box, Modal, Button, Stack, TextField, Checkbox, Typography } from '@mui/material';
import { updateGenericData } from './api';
import { useUnidades } from '../hooks/useUnidades';
import { cores, RowContainer, AutoComplete } from '../styles/styleUtils';
import MaskCpfCnpj from './helpers/MaskCpfCnpj';

const Input = styled.input`
  padding: 8px;
  border: 1px solid #ced4da;
  border-radius: 15px;
  font-size: 14px;
  width: 500px;
  box-sizing: border-box;

  &:focus {
    border-color: ${cores.laranja};
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(224, 110, 0, 0.25);
  }
`;

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '70vw',
  height: '95vh',
  '@media screen and (max-width: 600px)': {
    width: '90vw',
    height: '95vh',
  },
  bgcolor: 'transparent',
  border: `1px solid ${cores.chumbo}`,
  borderTop: `4px solid ${cores.laranja}`,
  boxShadow: 24,
  p: 2,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  background: `linear-gradient(180deg, ${cores.painelTopo} 0%, ${cores.painelBase} 100%)`,
  alignItems: 'center',
  justifyContent: 'center',
};

const RadioButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-right: 15px;
`;

const RadioButton = styled.input`
  margin-right: 5px;
`;

const BaseLabel = styled.h1`
  font-size: 0.9em;
  color: ${cores.textoClaro};
  width: 150px;
`;

const RadioLabel = styled.label`
  font-size: 0.9em;
  color: ${cores.textoClaro};
  font-weight: bold;
  line-height: 10px;
`;

const Select = styled.select`
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 10px;
  font-size: 14px;
  width: 80%;
  box-sizing: border-box;
`;

const SETORES = [
  { value: 'vendas', label: 'Comercial' },
  { value: 'engenharia', label: 'Engenharia' },
  { value: 'montagem', label: 'Montagem / Operações' },
  { value: 'administrativo', label: 'Administrativo / Financeiro' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'diretoria', label: 'Diretoria' },
  { value: 'estoque', label: 'Estoque / Logística' },
  { value: 'tecnologia', label: 'Tecnologia / Sistemas' },
  { value: 'obra', label: 'Obra' },
  { value: 'ativosimobilizados', label: 'Ativo Imobilizado' },
];

const GRUPOS_POR_SETOR = {
  vendas: [
    { value: 'equipecomercial', label: 'Equipe Comercial' },
    { value: 'comissoes', label: 'Comissões' },
    { value: 'prospeccao', label: 'Prospecção' },
    { value: 'viagenscomerciais', label: 'Viagens Comerciais' },
  ],
  engenharia: [
    { value: 'projetos', label: 'Projetos' },
    { value: 'homologacoes', label: 'Homologações' },
    { value: 'laudosvistorias', label: 'Laudos / Vistorias' },
    { value: 'engenhariaterceirizada', label: 'Engenharia Terceirizada' },
  ],
  montagem: [
    { value: 'equipecampo', label: 'Equipe de Campo' },
    { value: 'ferramentas', label: 'Ferramentas' },
    { value: 'epi', label: 'EPI' },
    { value: 'hospedagemalimentacao', label: 'Hospedagem / Alimentação' },
    { value: 'fretes', label: 'Fretes' },
  ],
  administrativo: [
    { value: 'estruturaadministrativa', label: 'Estrutura Administrativa' },
    { value: 'contabilidade', label: 'Contabilidade' },
    { value: 'juridico', label: 'Jurídico' },
    { value: 'tarifasbancarias', label: 'Tarifas Bancárias' },
    { value: 'softwaresaas', label: 'Software / SaaS' },
  ],
  marketing: [
    { value: 'trafegopago', label: 'Tráfego Pago' },
    { value: 'agenciadesign', label: 'Agência / Design' },
    { value: 'eventos', label: 'Eventos' },
    { value: 'materialpromocional', label: 'Material Promocional' },
  ],
  diretoria: [
    { value: 'gestao', label: 'Gestão' },
    { value: 'consultorias', label: 'Consultorias' },
    { value: 'viagensdiretoria', label: 'Viagens / Representação' },
  ],
  estoque: [
    { value: 'armazenagem', label: 'Armazenagem' },
    { value: 'transporte', label: 'Transporte' },
    { value: 'inventario', label: 'Inventário' },
    { value: 'perdasavarias', label: 'Perdas / Avarias' },
  ],
  tecnologia: [
    { value: 'hospedagemnuvem', label: 'Hospedagem / Nuvem' },
    { value: 'licencas', label: 'Licenças' },
    { value: 'desenvolvimento', label: 'Desenvolvimento' },
    { value: 'suporte', label: 'Suporte' },
  ],
  obra: [
    { value: 'materiais', label: 'Materiais' },
    { value: 'maodeobra', label: 'Mão de Obra' },
    { value: 'locacao', label: 'Locação' },
    { value: 'deslocamento', label: 'Deslocamento' },
  ],
  ativosimobilizados: [
    { value: 'veiculos', label: 'Veículos' },
    { value: 'informatica', label: 'Informática' },
    { value: 'moveisequipamentos', label: 'Móveis / Equipamentos' },
  ],
};

const GRUPOS_VEICULO = [
  { value: 'operacional', label: 'Operacional / Montagem' },
  { value: 'comercial', label: 'Comercial' },
  { value: 'administrativo', label: 'Administrativo' },
  { value: 'diretoria', label: 'Diretoria' },
];

const CARGOS = [
  { value: 'gerente', label: 'Gerente' },
  { value: 'montador', label: 'Montador' },
  { value: 'liderequipe', label: 'Líder de Equipe' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'consultorinterno', label: 'Consultor Interno' },
  { value: 'consultorexterno', label: 'Consultor Externo' },
  { value: 'recepcionista', label: 'Recepcionista' },
  { value: 'engenheiro', label: 'Engenheiro' },
  { value: 'proprietario', label: 'Proprietário' },
];

const ModalAddCentroCusto = React.forwardRef((props, ref) => {
  const [data, setData] = useState(() => ({
    ativo: true,
    tipo: 'N',
    setor: '',
    grupo: '',
    ...props.dataSelected,
  }));
  const { unidades } = useUnidades();

  const gruposDisponiveis = useMemo(() => {
    if (data.tipo === 'V') return GRUPOS_VEICULO;
    return GRUPOS_POR_SETOR[data.setor] || [];
  }, [data.tipo, data.setor]);

  const handleChange = (e) => {
    const { id, value } = e.target;

    if (id === 'setor') {
      setData((prev) => ({ ...prev, setor: value, grupo: '' }));
      return;
    }

    setData((prev) => ({ ...prev, [id]: value }));
  };

  const handleTypeChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'tipo' ? { grupo: '' } : {}),
    }));
  };

  const handleAutoCompleteChange = (name, value) => {
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const saveData = async () => {
    await updateGenericData('centrocusto', data);
    props.onConfirm();
  };

  const handleChangeCpf = (e) => {
    const newCpf = e.target.value.replace(/\D/g, '');
    setData((prev) => ({ ...prev, cpf: newCpf }));
  };

  const renderSetorEGrupo = ({ mostrarSetor = true, grupoVeiculo = false } = {}) => (
    <>
      {mostrarSetor && (
        <RowContainer>
          <BaseLabel htmlFor="setor">Setor</BaseLabel>
          <Select id="setor" name="setor" value={data.setor || ''} onChange={handleChange}>
            <option value="">Selecione</option>
            {SETORES.map((setor) => (
              <option key={setor.value} value={setor.value}>
                {setor.label}
              </option>
            ))}
          </Select>
        </RowContainer>
      )}

      <RowContainer>
        <BaseLabel htmlFor="grupo">Grupo</BaseLabel>
        <Select
          id="grupo"
          name="grupo"
          value={data.grupo || ''}
          onChange={handleChange}
          disabled={!grupoVeiculo && !data.setor}
        >
          <option value="">{!grupoVeiculo && !data.setor ? 'Selecione o setor primeiro' : 'Selecione'}</option>

          {data.grupo === 'custofixo' && (
            <option value="custofixo">Custos Fixos (legado)</option>
          )}
          {data.grupo === 'comercial' && data.tipo !== 'V' && (
            <option value="comercial">Equipe Comercial (legado)</option>
          )}

          {gruposDisponiveis.map((grupo) => (
            <option key={grupo.value} value={grupo.value}>
              {grupo.label}
            </option>
          ))}
        </Select>
      </RowContainer>
    </>
  );

  return (
    <Modal open={props.open} onClose={props.onClose}>
      <Box sx={style} gap={2}>
        <Stack direction="row">
          <BaseLabel>Tipo Centro Custo</BaseLabel>

          <RadioButtonWrapper>
            <RadioButton type="radio" name="tipo" value="N" checked={data.tipo === 'N'} onChange={handleTypeChange} />
            <RadioLabel>Normal</RadioLabel>
          </RadioButtonWrapper>

          <RadioButtonWrapper>
            <RadioButton type="radio" name="tipo" value="C" checked={data.tipo === 'C'} onChange={handleTypeChange} />
            <RadioLabel>Colaborador</RadioLabel>
          </RadioButtonWrapper>

          <RadioButtonWrapper>
            <RadioButton type="radio" name="tipo" value="V" checked={data.tipo === 'V'} onChange={handleTypeChange} />
            <RadioLabel>Veículo</RadioLabel>
          </RadioButtonWrapper>
        </Stack>

        {data.tipo === 'N' && (
          <Stack>
            <RowContainer>
              <BaseLabel htmlFor="nome">Nome</BaseLabel>
              <Input type="text" id="nome" name="nome" value={data.nome || ''} onChange={handleChange} />
            </RowContainer>
            {renderSetorEGrupo()}
          </Stack>
        )}

        {data.tipo === 'C' && (
          <Stack>
            <Stack direction="row">
              <BaseLabel>Tipo Colaborador</BaseLabel>

              <RadioButtonWrapper>
                <RadioButton type="radio" name="tipoColaborador" value="F" checked={data.tipoColaborador === 'F'} onChange={handleTypeChange} />
                <RadioLabel>Pessoa Física</RadioLabel>
              </RadioButtonWrapper>

              <RadioButtonWrapper>
                <RadioButton type="radio" name="tipoColaborador" value="J" checked={data.tipoColaborador === 'J'} onChange={handleTypeChange} />
                <RadioLabel>Pessoa Jurídica</RadioLabel>
              </RadioButtonWrapper>
            </Stack>

            <RowContainer>
              <BaseLabel htmlFor="nome">Nome</BaseLabel>
              <Input type="text" id="nome" name="nome" value={data.nome || ''} onChange={handleChange} />
            </RowContainer>

            <RowContainer>
              <BaseLabel htmlFor="cpf">CPF/CNPJ</BaseLabel>
              <MaskCpfCnpj id="cpf" variant="outlined" value={data.cpf || ''} onChange={handleChangeCpf} sx={{ width: '80%', backgroundColor: 'white' }} />
            </RowContainer>

            {renderSetorEGrupo()}

            <RowContainer>
              <BaseLabel htmlFor="unidade">Unidade</BaseLabel>
              <AutoComplete
                disablePortal
                id="unidade"
                value={data.unidade || null}
                options={unidades}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                getOptionLabel={(option) => `${option.nome}`}
                renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
                onChange={(_, value) => handleAutoCompleteChange('unidade', value)}
              />
            </RowContainer>

            <RowContainer>
              <BaseLabel htmlFor="cargo">Cargo</BaseLabel>
              <Select id="cargo" name="cargo" value={data.cargo || ''} onChange={handleChange}>
                <option value="">Selecione</option>
                {CARGOS.map((cargo) => (
                  <option key={cargo.value} value={cargo.value}>{cargo.label}</option>
                ))}
              </Select>
            </RowContainer>
          </Stack>
        )}

        {data.tipo === 'V' && (
          <Stack>
            <Stack direction="row">
              <BaseLabel>Tipo Veículo</BaseLabel>
              {[
                ['carro', 'Carro'],
                ['moto', 'Moto'],
                ['caminhonete', 'Caminhonete'],
                ['caminhao', 'Caminhão'],
                ['carretinha', 'Carretinha'],
              ].map(([value, label]) => (
                <RadioButtonWrapper key={value}>
                  <RadioButton type="radio" name="tipoVeiculo" value={value} checked={data.tipoVeiculo === value} onChange={handleTypeChange} />
                  <RadioLabel>{label}</RadioLabel>
                </RadioButtonWrapper>
              ))}
            </Stack>

            <RowContainer>
              <BaseLabel htmlFor="setor">Setor</BaseLabel>
              <Select id="setor" name="setor" value={data.setor || ''} onChange={handleChange}>
                <option value="">Selecione</option>
                <option value="montagem">Montagem / Operações</option>
                <option value="vendas">Comercial</option>
                <option value="administrativo">Administrativo / Financeiro</option>
                <option value="diretoria">Diretoria</option>
              </Select>
            </RowContainer>

            {renderSetorEGrupo({ mostrarSetor: false, grupoVeiculo: true })}

            <RowContainer>
              <BaseLabel htmlFor="nome">Modelo</BaseLabel>
              <Input type="text" id="nome" name="nome" value={data.nome || ''} onChange={handleChange} />
            </RowContainer>

            <RowContainer>
              <BaseLabel htmlFor="anoFabricacao">Ano Fabricação</BaseLabel>
              <Input type="number" id="anoFabricacao" name="anoFabricacao" value={data.anoFabricacao || ''} onChange={handleChange} />
            </RowContainer>

            <RowContainer>
              <BaseLabel htmlFor="anoModelo">Ano Modelo</BaseLabel>
              <Input type="number" id="anoModelo" name="anoModelo" value={data.anoModelo || ''} onChange={handleChange} />
            </RowContainer>

            <RowContainer>
              <BaseLabel htmlFor="chassi">Chassi</BaseLabel>
              <Input type="text" id="chassi" name="chassi" value={data.chassi || ''} onChange={handleChange} />
            </RowContainer>

            <RowContainer>
              <BaseLabel htmlFor="placa">Placa</BaseLabel>
              <Input type="text" id="placa" name="placa" value={data.placa || ''} onChange={handleChange} />
            </RowContainer>

            <RowContainer>
              <BaseLabel htmlFor="valor">Valor</BaseLabel>
              <Input type="number" id="valor" name="valor" value={data.valor || ''} onChange={handleChange} />
            </RowContainer>

            <RowContainer>
              <BaseLabel htmlFor="quilometragem">Quilometragem</BaseLabel>
              <Input type="number" id="quilometragem" name="quilometragem" value={data.quilometragem || ''} onChange={handleChange} />
            </RowContainer>

            <RowContainer>
              <BaseLabel htmlFor="depreciacao">Depreciação</BaseLabel>
              <Input type="number" id="depreciacao" name="depreciacao" value={data.depreciacao || ''} onChange={handleChange} />
            </RowContainer>

            <RowContainer>
              <BaseLabel htmlFor="periodicidade">Periodicidade</BaseLabel>
              <Select id="periodicidade" name="periodicidade" value={data.periodicidade || ''} onChange={handleChange}>
                <option value="">Selecione</option>
                <option value="mensal">Mensal</option>
                <option value="trimestral">Trimestral</option>
                <option value="semestral">Semestral</option>
                <option value="anual">Anual</option>
              </Select>
            </RowContainer>
          </Stack>
        )}

        <Stack direction="row">
          <Checkbox
            checked={Boolean(data?.ativo)}
            onChange={() => setData((prev) => ({ ...prev, ativo: !prev.ativo }))}
            inputProps={{ 'aria-label': 'controlled' }}
            sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: cores.laranja } }}
          />
          <Typography mt={1} sx={{ color: cores.textoClaro }}>Ativo</Typography>
        </Stack>

        <Stack direction="row" justifyContent="space-between" gap={20} mt={0}>
          <Button variant="outlined" onClick={props.onClose} color="error">Cancelar</Button>
          <Button variant="contained" color="success" onClick={saveData}>Salvar</Button>
        </Stack>
      </Box>
    </Modal>
  );
});

ModalAddCentroCusto.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  dataSelected: PropTypes.object,
};

export default ModalAddCentroCusto;
